import BaseModal, {
	ModalContainerProps,
	ModalFooterProps,
	ModalTitleProps,
} from 'src/components/primitives/BaseModal/BaseModal';
import { cj, cm } from 'src/utils/classNames';
import { DrawerName, useDrawersStore } from 'src/stores/useDrawersStore';
import { ReactNode, useEffect } from 'react';
import { useRafState } from 'react-use';
import { Z_INDEX } from 'src/constants/zIndex';
import SideDrawerProvider, {
	useSideDrawer,
} from 'src/components/Drawers/BaseDrawers/SideDrawer/SideDrawerContext';
import { MODAL_BG_CLASSNAME } from 'src/components/primitives/BaseModal/consts';
import useSideDrawerStyle from 'src/components/Drawers/BaseDrawers/SideDrawer/hooks/useSideDrawer';

interface SideDrawerContainerWrapperProps extends Omit<
	ModalContainerProps,
	'disableOverlay' | 'style'
> {
	drawerName: DrawerName;
}

function SideDrawerContainerWrapper({
	drawerName,
	onClose,
	...rest
}: SideDrawerContainerWrapperProps) {
	return (
		<SideDrawerProvider onClose={onClose} drawerName={drawerName}>
			<SideDrawerContainer drawerName={drawerName} {...rest} />
		</SideDrawerProvider>
	);
}

type SideDrawerContainerProps = Omit<
	SideDrawerContainerWrapperProps,
	'onClose'
>;

function SideDrawerContainer({
	className,
	drawerName,
	...rest
}: SideDrawerContainerProps) {
	const isDrawerClosing = useDrawersStore((s) => s.drawerIsTransitionClosing);
	const isDrawerOpen = useDrawersStore((s) => s[drawerName].show);
	const style = useSideDrawerStyle();
	const { close } = useSideDrawer();

	// Set is open on the next frame so our transition doesn't get clobbered.
	const [isOpenRaf, setIsOpenRaf] = useRafState(false);
	useEffect(() => {
		setIsOpenRaf(isDrawerOpen && !isDrawerClosing);
	}, [isDrawerOpen, isDrawerClosing]);

	return (
		<>
			<BaseModal.Container
				className={cm(
					'h-auto w-full rounded-none shadow-lg sm:w-[400px] sm:max-w-[400px] sm:rounded-l-sm',
					'border border-r-0 border-stroke-divider',
					'fixed right-0 bottom-0 transition-transform duration-100',
					isOpenRaf ? 'translate-x-0' : 'translate-x-full',
					MODAL_BG_CLASSNAME,
					Z_INDEX.modalContainer,
					className
				)}
				style={style}
				onClose={close}
				// We handle the overlay manually.
				disableOverlay
				{...rest}
			/>

			{/* Overlay */}
			{isDrawerOpen && (
				<div
					className={cj('fixed inset-0', Z_INDEX.modalOverlay)}
					onClick={close}
				/>
			)}
		</>
	);
}

type SideDrawerTitleProps<T extends string> = Omit<
	ModalTitleProps<T>,
	'onClose'
>;

function SideDrawerTitle<T extends string>(props: SideDrawerTitleProps<T>) {
	const { close } = useSideDrawer();

	return <BaseModal.Title onClose={close} {...(props as ModalTitleProps<T>)} />;
}

interface ResponsiveModalFooterProps extends Omit<
	ModalFooterProps,
	'children'
> {
	/**
	 * Can either be a plain `ReactNode` or, if you need access to the `close`
	 * function (typically needed when closing from a footer button), a render
	 * function that will receive `close` as an argument.
	 */
	children: ReactNode | ((props: { close: () => void }) => ReactNode);
}

function SideDrawerFooter({ children, ...rest }: ResponsiveModalFooterProps) {
	const { close } = useSideDrawer();

	const content =
		typeof children === 'function' ? children({ close }) : children;

	return <BaseModal.Footer {...rest}>{content}</BaseModal.Footer>;
}

export default {
	/**
	 * Renders a drawer that slides in from the right side of the screen.
	 * Simply pass the `drawerName` you want to use to `Container` and the drawer will
	 * internally handle the close / open transition.
	 */
	Container: SideDrawerContainerWrapper,
	Title: SideDrawerTitle,
	Body: BaseModal.Body,
	Footer: SideDrawerFooter,
};
